import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { setNavigator } from '../services/navigator';

const AuthContext = createContext({
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  login: async () => {},
  logout: () => {},
  setUser: () => {},
  setIsAuthenticated: () => {}
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Fonction pour forcer la mise à jour du contexte
  const updateAuthState = useCallback((newState) => {
    if (newState.user !== undefined) setUser(newState.user);
    if (newState.isAuthenticated !== undefined) setIsAuthenticated(newState.isAuthenticated);
    if (newState.loading !== undefined) setLoading(newState.loading);
  }, []);

  // Initialiser le navigateur
  useEffect(() => {
    setNavigator(navigate);
    
    return () => {
      setNavigator(null);
    };
  }, [navigate]);

  // Vérifier l'authentification au chargement
  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      const userType = localStorage.getItem('userType');
      
    
      if (!token || !userData) {
        if (process.env.NODE_ENV === "development") {
          console.debug("[AuthContext] Aucun token/utilisateur dans le localStorage")
        }
        setIsAuthenticated(false);
        return false;
      }
      
      // Si on est déjà sur la page de login, ne pas essayer de vérifier l'auth
      if (window.location.pathname === '/login') {
        return false;
      }
      
      try {
        
        let profile;
        // Vérifier la validité du token avec une requête au serveur appropriée selon le type d'utilisateur
        try {
          if (userType === 'demenageur') {
            profile = await api.getUserProfile(); // Utiliser l'endpoint approprié pour les déménageurs
          } else {
            profile = await api.getClientProfile(); // Pour les clients
          }
        } catch (profileError) {
          console.warn('Erreur lors de la récupération du profil, utilisation des données locales:', profileError);
          // Si la récupération du profil échoue, utiliser les données locales
          const localUser = JSON.parse(userData);
          setUser(localUser);
          setIsAuthenticated(true);
          setLoading(false);
          return true;
        }
        
        
        // Normaliser les données du profil
        const localUserData = JSON.parse(userData);
        const normalizedProfile = {
          ...localUserData, // Conserver les données existantes
          ...profile, // Mettre à jour avec les données du serveur
          id: profile.id || localUserData.id,
          email: profile.email || localUserData.email,
          role: userType || localUserData.role
        };
        
        
        // Mettre à jour l'état avec les données du profil
        setUser(normalizedProfile);
        setIsAuthenticated(true);
        
        // Sauvegarder dans le localStorage
        localStorage.setItem('user', JSON.stringify(normalizedProfile));
        
        return true;
      } catch (error) {
        // Si l'erreur est une 401, déconnecter proprement
        if (error.message && error.message.includes('401')) {
          console.warn('Session expirée ou token invalide, déconnexion...', error);
          // Nettoyer les données d'authentification invalides
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('userType');
          setIsAuthenticated(false);
          setUser(null);
          
          // Ne rediriger que si on n'est pas déjà sur la page de login
          if (window.location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
          return false;
        }
        // Pour les autres erreurs, on laisse passer mais on log l'erreur
        console.error('Erreur lors de la vérification du profil utilisateur:', error);
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Exécuter la vérification d'authentification au chargement
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await checkAuth();
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'authentification:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    
    verifyAuth();
  }, [checkAuth]);

  // Se connecter
  const login = useCallback(async (user, token) => {
    try {
      if (!user || !token) {
        throw new Error('Informations de connexion manquantes');
      }
      
      setLoading(true);

      // Normaliser les données utilisateur
      const normalizedUser = {
        id: user.id,
        email: user.email || '',
        role: user.role || 'client',
        firstName: user.first_name || user.firstName || '',
        lastName: user.last_name || user.lastName || '',
        phone: user.phone || '',
        avatar: user.avatar_url || user.avatar || null,
        isVerified: user.is_verified || user.isVerified || false,
        address: user.address || null,
        city: user.city || null,
        postalCode: user.postal_code || user.postalCode || null,
        country: user.country || null,
        createdAt: user.created_at || user.createdAt || null,
        updatedAt: user.updated_at || user.updatedAt || null,
        ...(user.company_name && { companyName: user.company_name }),
        ...(user.siret && { siret: user.siret })
      };
      
      // Stocker le token et les données utilisateur normalisées
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      localStorage.setItem('userType', normalizedUser.role);
      
      // Récupérer le profil serveur (assure cohérence et champs d'essai)
      let serverProfile = null;
      try {
        if ((normalizedUser.role || '').toLowerCase() === 'demenageur') {
          serverProfile = await api.getUserProfile();
        } else {
          serverProfile = await api.getClientProfile();
        }
      } catch (e) {
        console.warn('Impossible de récupérer le profil serveur après login, utilisation des données locales:', e);
        // En cas d'erreur, utiliser les données locales pour ne pas bloquer la connexion
        serverProfile = null;
      }

      const mergedUser = {
        ...normalizedUser,
        ...(serverProfile && typeof serverProfile === 'object' ? serverProfile : {}),
        id: (serverProfile && serverProfile.id) || normalizedUser.id,
        email: (serverProfile && serverProfile.email) || normalizedUser.email,
        role: normalizedUser.role
      };

      // Mettre à jour le contexte
      setUser(mergedUser);
      setIsAuthenticated(true);
      setLoading(false);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      // Nettoyer en cas d'erreur
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      
      return { 
        success: false, 
        message: error.message || 'Erreur lors de la connexion' 
      };
    }
  }, []);

  // S'inscrire
  const register = useCallback(async (userData) => {
    try {
      const response = await api.register(userData)

      if (response?.success) {
        return {
          success: true,
          message:
            response.message ||
            "Inscription réussie. Vérifiez votre email puis connectez-vous.",
        }
      }
      
      return { success: false, message: response?.message || 'Erreur lors de l\'inscription' };
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      return { 
        success: false, 
        message: error.message || 'Erreur lors de l\'inscription' 
      };
    }
  }, []);

  // Se déconnecter
  const logout = useCallback(() => {
    try {
      // Supprimer les données de l'utilisateur
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      
      // Réinitialiser l'état
      setUser(null);
      setIsAuthenticated(false);
      
      // Vérifier si on est déjà sur la page de login pour éviter les boucles
      if (window.location.pathname !== '/login') {
        // Utiliser replace: true pour éviter d'ajouter à l'historique de navigation
        navigate('/login', { replace: true, state: { from: null } });
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // En dernier recours, forcer le rechargement
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  }, [navigate]);

  // Récupérer le token du localStorage
  const [token, setTokenState] = useState(() => localStorage.getItem('token'));

  // Mettre à jour le token dans le state lorsqu'il change dans le localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        setTokenState(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Mettre à jour le token dans le localStorage et dans le state
  const setToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
    setTokenState(newToken);
  }, []);

  // Mettre à jour les données utilisateur
  const updateUser = useCallback((userData) => {
    setUser(prev => ({
      ...prev,
      ...userData
    }));
    
    // Mettre à jour également dans le localStorage
    let currentUser = {};
    try {
      const raw = localStorage.getItem('user');
      if (raw && raw !== "undefined" && raw !== "null") {
        currentUser = JSON.parse(raw);
      }
    } catch (_) {
      currentUser = {};
    }
    localStorage.setItem('user', JSON.stringify({ ...currentUser, ...userData }));
  }, []);

  // Valeur du contexte avec des références stables
  const contextValue = React.useMemo(() => ({
    user,
    token,
    isAuthenticated,
    loading,
    login,
    logout,
    register,
    updateUser,
    setUser: (user) => updateAuthState({ user }),
    setIsAuthenticated: (isAuthenticated) => updateAuthState({ isAuthenticated }),
    setToken,
    updateAuthState
  }), [user, token, isAuthenticated, loading, login, logout, register, updateUser, setToken, updateAuthState]);

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Hook personnalisé pour utiliser le contexte d'authentification
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
}

export default AuthContext;
